import { useEffect, useMemo } from "react";
import logoGoldsand from "@/assets/logo-goldsand-v2.png";
import { isAppWebView, isLikelyMobileBrowser } from "@/utils/webview";

function getDeepLinkHref(search: string) {
  return `goldsand://return-to-app${search}`;
}

interface PageContentProps {
  redirectToApp: boolean;
}

export function PageContent({ redirectToApp }: PageContentProps) {
  const deepLinkHref = useMemo(() => getDeepLinkHref(window.location.search), []);
  if (redirectToApp) return (
    <>
      <h1 className="-mt-6 text-lg font-bold">Returning to Goldsand...</h1>
      <p className="text-base text-muted-fg leading-relaxed">
        If you&apos;re not sent back automatically, tap the button below.
      </p>
      <a
        id="manual-return"
        href={deepLinkHref}
        className="btn-primary mt-xs h-[48px] px-[32px] rounded-full bg-foreground text-primary-fg text-base font-semibold inline-flex items-center justify-center cursor-pointer shadow-[0_2px_8px_rgba(0,0,0,0.12)]"
      >
        Return to Goldsand
      </a>
    </>
  );

  // Viewing the page on desktop / non-mobile user-agent
  return (
    <>
      <h1 className="-mt-6 text-lg font-bold">Open this on your phone</h1>
      <p className="text-base text-muted-fg leading-relaxed">
        This link is meant to send you back to the Goldsand app. Open it
        on your phone to continue.
      </p>
    </>
  );
}

export default function ReturnToApp() {
  const deepLinkHref = useMemo(() => getDeepLinkHref(window.location.search), []);
  const inAppWebView = isAppWebView();
  const shouldAttemptRedirectToApp = inAppWebView || isLikelyMobileBrowser();

  useEffect(() => {
    if (!shouldAttemptRedirectToApp) return;

    const timeoutId = window.setTimeout(() => {
      window.location.replace(deepLinkHref);
    }, 50);

    return () => window.clearTimeout(timeoutId);
  }, [deepLinkHref, shouldAttemptRedirectToApp]);

  return (
    <div className="min-h-dvh flex items-center justify-center bg-background text-foreground px-lg">
      <div className="max-w-[420px] w-full flex flex-col items-center text-center gap-md">
        <img
          src={logoGoldsand}
          alt="Goldsand logo"
          className="h-[150px] rounded-[16px] object-cover"
        />
        <PageContent redirectToApp={shouldAttemptRedirectToApp} />
      </div>
    </div>
  );
}
